import { Injectable, inject } from "@angular/core";

import { HttpClient } from "@angular/common/http";

import { Observable, forkJoin, of } from "rxjs";

import { catchError, map, switchMap } from "rxjs/operators";

import { HospitalInvoiceMasterService } from "src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-billing/hospital-billing.service";

import { HospitalInvoiceDetailService } from "src/app/hospital/BackOffice/hospital-master/invoice-detail/invoice-detail.service";

import { HospitalInvoiceMasterReadAllPaginatedRequestDTO } from "src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-billing/hospital-billing.interface";

import { PetientQuickRegistrationService } from "src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-patient-master/patient-quick-registration/petient-quick-registration.service";

import { TreatmentPlanMasterService } from "src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-treatment-plan/hospital-treatment-plan.service";

import { RevenueReport_Create } from "src/app/GlobalVariable";

import { EncryptionServiceService } from "src/app/Common/EncryptionService/encryption-service.service";

import {
  RevenueReportRequestDTO,
  RevenueReportRow,
} from "./revenue-report-interface";

@Injectable({
  providedIn: "root",
})
export class RevenueReportServiceService {
  private http = inject(HttpClient);

  private encryptionService = inject(EncryptionServiceService);

  private hospitalInvoiceMasterService = inject(HospitalInvoiceMasterService);

  private hospitalInvoiceDetailService = inject(HospitalInvoiceDetailService);

  /*
   * Existing services only.
   *
   * OPD doctor is stored on PatientVisit.
   * IPD doctor is stored on TreatmentPlanMaster.
   */
  private patientVisitService = inject(PetientQuickRegistrationService);

  private treatmentPlanMasterService = inject(TreatmentPlanMasterService);

  constructor() {}

  // ============================================================
  // REVENUE REPORT
  // ============================================================

  getRevenueReport(
    request: RevenueReportRequestDTO & { userId?: number },
  ): Observable<RevenueReportRow[]> {
    /*
     * SINGLE API FIX:
     *
     * The dedicated RevenueReport microservice already executes
     * hm.RevenueReportForOwner_Read, which returns hospital, invoice,
     * patient, doctor, payment and revenue information in one result set.
     *
     * When userId is supplied by the Revenue Report component, use that
     * single API and return immediately. The complete previous invoice
     * fan-out flow below is intentionally kept as a legacy fallback so no
     * existing service logic is removed for any older caller.
     */
    const loggedInUserId = Number(request?.userId || 0);

    if (loggedInUserId > 0) {
      return this.getRevenueReportFromSingleApi(request, loggedInUserId);
    }

    const currentHospitalId = Number(request?.hospitalId || 0);

    if (!currentHospitalId) {
      return of([]);
    }

    const payload: HospitalInvoiceMasterReadAllPaginatedRequestDTO = {
      hospitalId: currentHospitalId,

      pageNo: 1,

      pageSize: 100000,

      orderByClause: "PrimaryDesc",

      /*
       * Keep existing Invoice API flow.
       *
       * Do NOT push custom SQL/filter syntax here.
       * Revenue filtering remains in component.
       */
      whereClause: "",
    };

    return this.hospitalInvoiceMasterService
      .HospitalInvoiceMaster_ReadAllPaginated(payload)
      .pipe(
        // ------------------------------------------------------
        // 1. Hospital + Date filtering
        // ------------------------------------------------------

        map((response: any) => {
          const items = Array.isArray(response?.items) ? response.items : [];

          /*
           * API is already called using HospitalId.
           *
           * Additional safeguard:
           * when HospitalId is returned in each row,
           * ensure it matches logged-in owner's HospitalId.
           */
          const hospitalInvoices = items.filter((invoice: any) => {
            const returnedHospitalId = Number(
              invoice?.hospitalId ?? invoice?.HospitalId ?? 0,
            );

            /*
             * Some existing paginated responses may not return
             * HospitalId even though HospitalId was supplied
             * to the API.
             */
            if (returnedHospitalId <= 0) {
              return true;
            }

            return returnedHospitalId === currentHospitalId;
          });

          return hospitalInvoices.filter((invoice: any) =>
            this.isInvoiceInsideSelectedDate(
              invoice,
              request.fromDate,
              request.toDate,
            ),
          );
        }),

        // ------------------------------------------------------
        // 2. Load COMPLETE invoice information
        //
        // Paginated invoice API gives list/grid fields.
        // ReadById gives paymentMode / OtherDetail data.
        // ------------------------------------------------------

        switchMap((invoices: any[]) => {
          if (!invoices.length) {
            return of([]);
          }

          const requests = invoices.map((invoice: any) => {
            const invoiceId = Number(
              invoice?.invoiceId ?? invoice?.InvoiceId ?? 0,
            );

             const hospitalId = Number(
    invoice?.hospitalId ?? invoice?.HospitalId ?? 0,
  );
            const patientType = String(
              invoice?.patientType ?? invoice?.PatientType ?? "",
            )
              .trim()
              .toUpperCase();

            const billedTo = Number(
              invoice?.billedTo ??
                invoice?.BilledTo ??
                invoice?.patientId ??
                invoice?.PatientId ??
                0,
            );

            /*
             * Full InvoiceMaster.
             *
             * Required mainly because PaymentMode / OtherDetail6
             * may not be returned by ReadAllPaginated.
             */
            const masterRequest =
              invoiceId > 0
                ? this.hospitalInvoiceMasterService
                    .HospitalInvoiceMaster_ReadById({
                      invoiceId,
                      hospitalId,
                    })
                    .pipe(catchError(() => of(invoice)))
                : of(invoice);

            /*
             * Invoice Detail.
             *
             * Doctor selected in Billing Calculator is stored
             * as StaffId in HospitalInvoiceDetail.
             */
            const detailsRequest =
              invoiceId > 0
                ? this.hospitalInvoiceDetailService
                    .HospitalInvoiceDetail_ReadByInvoiceId({
                      invoiceId,
                    })
                    .pipe(
                      catchError(() =>
                        of({
                          items: [],
                        }),
                      ),
                    )
                : of({
                    items: [],
                  });

            /*
             * OPD:
             *
             * Your existing invoice-detail component uses:
             *
             * PatientVisit_ReadById({
             *     visitId: patientId
             * })
             *
             * Therefore OPD BilledTo / PatientId can be used
             * to get the visit and its DoctorId.
             */
            const opdVisitRequest =
              patientType === "OPD" && billedTo > 0
                ? this.patientVisitService
                    .PatientVisit_ReadById({
                      visitId: billedTo,
                    })
                    .pipe(catchError(() => of(null)))
                : of(null);

            /*
             * IPD:
             *
             * TreatmentPlanMaster contains DoctorId.
             * Your IPD admission already stores selected doctor
             * there.
             */
            const ipdTreatmentRequest =
              patientType === "IPD" && billedTo > 0
                ? this.treatmentPlanMasterService
                    .TreatmentPlanMaster_ReadByPatientId({
                      patientId: billedTo,
                    })
                    .pipe(
                      catchError(() =>
                        of({
                          items: [],
                        }),
                      ),
                    )
                : of({
                    items: [],
                  });

            return forkJoin({
              fullInvoice: masterRequest,

              invoiceDetails: detailsRequest,

              opdVisit: opdVisitRequest,

              treatmentPlans: ipdTreatmentRequest,
            }).pipe(
              map((result: any) =>
                this.mapRevenueRow(
                  invoice,

                  result?.fullInvoice ?? invoice,

                  result?.invoiceDetails?.items ?? [],

                  result?.opdVisit ?? null,

                  result?.treatmentPlans?.items ?? [],

                  currentHospitalId,
                ),
              ),

              /*
               * One bad invoice must not stop
               * complete owner report.
               */
              catchError(() =>
                of(
                  this.mapRevenueRow(
                    invoice,
                    invoice,
                    [],
                    null,
                    [],
                    currentHospitalId,
                  ),
                ),
              ),
            );
          });

          return forkJoin(requests);
        }),

        // ------------------------------------------------------
        // 3. Owner Hospital final safeguard
        // ------------------------------------------------------

        map((rows: RevenueReportRow[]) =>
          rows.filter(
            (row) => Number(row?.hospitalId || 0) === currentHospitalId,
          ),
        ),

        // ------------------------------------------------------
        // 4. Latest first
        // ------------------------------------------------------

        map((rows: RevenueReportRow[]) =>
          rows.sort(
            (a, b) =>
              this.getTime(b.receivedDate) - this.getTime(a.receivedDate),
          ),
        ),
      );
  }

  // ============================================================
  // SINGLE REVENUE REPORT API
  // ============================================================

  private getRevenueReportFromSingleApi(
    request: RevenueReportRequestDTO & { userId?: number },
    loggedInUserId: number,
  ): Observable<RevenueReportRow[]> {
    const requestedHospitalId = Number(request?.hospitalId || 0);

    const payload = {
      userId: loggedInUserId,

      /*
       * First screen load sends null so the stored procedure resolves the
       * owner's hospital from UserId. Once resolved, subsequent filters can
       * send the same HospitalId and the stored procedure validates it.
       */
      hospitalId: requestedHospitalId > 0 ? requestedHospitalId : null,

      fromDate: request?.fromDate,

      toDate: request?.toDate,
    };

    return this.http.post<any>(RevenueReport_Create, payload).pipe(
      /*
       * IMPORTANT FIX:
       *
       * In some environments the common interceptor already decrypts the
       * response. In others this endpoint can still reach the service as an
       * EncData/TokenKey wrapper. Support both without changing the global
       * interceptor or any other API.
       */
      map((response: any) =>
        this.decryptRevenueReportResponseIfRequired(response),
      ),

      /*
       * IMPORTANT FIX:
       *
       * Do not assume that RevenueReport/Create returns only { items: [] }.
       * Depending on the APIResponse/encryption layer the stored-procedure
       * rows can be under items/data/result/response/body/payload, nested SQL
       * result sets, or JSON strings. Recursively locate the actual invoice
       * rows so valid DB data is not converted into an empty UI array.
       */
      map((response: any) => this.extractSingleApiItems(response)),

      map((items: any[]) =>
        items.map((item: any) => this.mapSingleApiRevenueRow(item)),
      ),

      /*
       * Keep the exact existing UI expectation: latest received invoice first.
       */
      map((rows: RevenueReportRow[]) =>
        rows.sort(
          (a, b) =>
            this.getTime(b.receivedDate) - this.getTime(a.receivedDate),
        ),
      ),
    );
  }

  private decryptRevenueReportResponseIfRequired(response: any): any {
    const parsedResponse = this.parsePossibleJson(response);

    const encData = String(
      parsedResponse?.encData ?? parsedResponse?.EncData ?? "",
    ).trim();

    const tokenKey = String(
      parsedResponse?.tokenKey ?? parsedResponse?.TokenKey ?? "",
    ).trim();

    /*
     * Common interceptor already decrypted it, or backend returned normal JSON.
     */
    if (!encData || !tokenKey) {
      return parsedResponse;
    }

    const decryptResponse = (this.encryptionService as any)?.decryptResponse;

    if (typeof decryptResponse !== "function") {
      throw new Error(
        "Revenue Report returned encrypted data, but decryptResponse is unavailable.",
      );
    }

    const encryptedWrapper = {
      ...parsedResponse,
      encData,
      tokenKey,
      EncData: encData,
      TokenKey: tokenKey,
    };

    let decryptedValue: any;

    try {
      /*
       * Existing Nishwik branches use either:
       *   decryptResponse(wrapper)
       * or
       *   decryptResponse(encData, tokenKey)
       */
      decryptedValue =
        decryptResponse.length >= 2
          ? decryptResponse.call(this.encryptionService, encData, tokenKey)
          : decryptResponse.call(this.encryptionService, encryptedWrapper);
    } catch (error: any) {
      throw new Error(
        error?.message || "Revenue Report response could not be decrypted.",
      );
    }

    if (
      decryptedValue &&
      (typeof decryptedValue?.then === "function" ||
        typeof decryptedValue?.subscribe === "function")
    ) {
      throw new Error(
        "Revenue Report decryptResponse returned an unsupported async value.",
      );
    }

    const decryptedResponse = this.parsePossibleJson(decryptedValue);

    const returnedEncData = String(
      decryptedResponse?.encData ?? decryptedResponse?.EncData ?? "",
    ).trim();

    if (returnedEncData && returnedEncData === encData) {
      throw new Error("Revenue Report response is still encrypted.");
    }

    return decryptedResponse;
  }

  private parsePossibleJson(value: any): any {
    let parsedValue = value;

    for (let attempt = 0; attempt < 3; attempt++) {
      if (typeof parsedValue !== "string") {
        return parsedValue;
      }

      const trimmedValue = parsedValue.trim();

      if (!trimmedValue) {
        return "";
      }

      try {
        parsedValue = JSON.parse(trimmedValue);
      } catch {
        return trimmedValue;
      }
    }

    return parsedValue;
  }

  private extractSingleApiItems(response: any): any[] {
    const parsedResponse = this.parsePossibleJson(response);

    const foundRows: any[] = [];
    const seenInvoiceKeys = new Set<string>();
    const visitedObjects = new Set<any>();

    const addRevenueRow = (item: any): void => {
      const invoiceId = Number(item?.invoiceId ?? item?.InvoiceId ?? 0);
      const hospitalId = Number(item?.hospitalId ?? item?.HospitalId ?? 0);

      if (invoiceId <= 0 || hospitalId <= 0) {
        return;
      }

      const key = `${hospitalId}|${invoiceId}`;

      if (seenInvoiceKeys.has(key)) {
        return;
      }

      seenInvoiceKeys.add(key);
      foundRows.push(item);
    };

    const walk = (value: any): void => {
      if (value === null || value === undefined) {
        return;
      }

      if (typeof value === "string") {
        const parsedValue = this.tryParseJson(value);

        if (parsedValue !== null) {
          walk(parsedValue);
        }

        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item: any) => walk(item));
        return;
      }

      if (typeof value !== "object") {
        return;
      }

      if (visitedObjects.has(value)) {
        return;
      }

      visitedObjects.add(value);

      /*
       * The SP returns one object per invoice. If this object is already a
       * revenue row, collect it directly.
       */
      if (this.isSingleApiRevenueRow(value)) {
        addRevenueRow(value);
        return;
      }

      /*
       * Check the common response-wrapper properties first.
       */
      const preferredContainerKeys = [
        "items",
        "Items",
        "data",
        "Data",
        "result",
        "Result",
        "results",
        "Results",
        "response",
        "Response",
        "body",
        "Body",
        "payload",
        "Payload",
        "value",
        "Value",
        "content",
        "Content",
        "returnValue",
        "ReturnValue",
      ];

      const walkedKeys = new Set<string>();

      preferredContainerKeys.forEach((key: string) => {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          walkedKeys.add(key);
          walk(value[key]);
        }
      });

      /*
       * SQL/Dapper handlers can also expose an anonymous nested result-set
       * property. Walk remaining object/array/string values as a safe fallback.
       */
      Object.keys(value).forEach((key: string) => {
        if (walkedKeys.has(key)) {
          return;
        }

        if (
          key === "encData" ||
          key === "EncData" ||
          key === "tokenKey" ||
          key === "TokenKey"
        ) {
          return;
        }

        const childValue = value[key];

        if (
          Array.isArray(childValue) ||
          (childValue !== null && typeof childValue === "object") ||
          typeof childValue === "string"
        ) {
          walk(childValue);
        }
      });
    };

    walk(parsedResponse);

    return foundRows;
  }

  private isSingleApiRevenueRow(value: any): boolean {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }

    const invoiceId = Number(value?.invoiceId ?? value?.InvoiceId ?? 0);
    const hospitalId = Number(value?.hospitalId ?? value?.HospitalId ?? 0);

    if (invoiceId <= 0 || hospitalId <= 0) {
      return false;
    }

    return (
      value?.invoiceNumber !== undefined ||
      value?.InvoiceNumber !== undefined ||
      value?.receivedDate !== undefined ||
      value?.ReceivedDate !== undefined ||
      value?.status !== undefined ||
      value?.Status !== undefined ||
      value?.totalReceived !== undefined ||
      value?.TotalReceived !== undefined
    );
  }

  private tryParseJson(value: string): any | null {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
      return null;
    }

    if (
      !trimmedValue.startsWith("{") &&
      !trimmedValue.startsWith("[") &&
      !trimmedValue.startsWith('"')
    ) {
      return null;
    }

    try {
      return JSON.parse(trimmedValue);
    } catch {
      return null;
    }
  }

  private mapSingleApiRevenueRow(item: any): RevenueReportRow {
    const hospitalId = Number(item?.hospitalId ?? item?.HospitalId ?? 0);

    const invoiceId = Number(item?.invoiceId ?? item?.InvoiceId ?? 0);

    const patientId = Number(
      item?.patientId ??
        item?.PatientId ??
        item?.billedTo ??
        item?.BilledTo ??
        0,
    );

    const doctorId = Number(
      item?.doctorReferredId ??
        item?.DoctorReferredId ??
        item?.doctorId ??
        item?.DoctorId ??
        item?.staffId ??
        item?.StaffId ??
        0,
    );

    const patientType = String(
      item?.patientType ?? item?.PatientType ?? "",
    )
      .trim()
      .toUpperCase();

    const status = String(item?.status ?? item?.Status ?? "").trim();

    const receivedDate =
      item?.receivedDate ??
      item?.ReceivedDate ??
      item?.invoiceDate ??
      item?.InvoiceDate ??
      item?.paymentDate ??
      item?.PaymentDate ??
      null;

    const rawPaymentMode = String(
      item?.paymentMode ?? item?.PaymentMode ?? "",
    ).trim();

    const mapped: any = {
      invoiceId,

      invoiceNumber: String(
        item?.invoiceNumber ?? item?.InvoiceNumber ?? "-",
      ),

      hospitalId,

      patientId,

      patientName:
        String(item?.patientName ?? item?.PatientName ?? "").trim() ||
        "No Patient Name",

      patientType: patientType || "-",

      invoiceDate:
        item?.invoiceDate ?? item?.InvoiceDate ?? receivedDate,

      receivedDate,

      doctorId,

      doctorName:
        String(
          item?.doctorReferred ??
            item?.DoctorReferred ??
            item?.doctorName ??
            item?.DoctorName ??
            item?.staffName ??
            item?.StaffName ??
            "",
        ).trim() || "-",

      paymentMode: this.normalizePaymentMode(rawPaymentMode) || "-",

      status,

      totalReceived: Number(
        item?.totalReceived ??
          item?.TotalReceived ??
          item?.invoiceAmount ??
          item?.InvoiceAmount ??
          0,
      ),

      prrn: item?.prrn ?? item?.PRRN,

      otherDetail1: item?.otherDetail1 ?? item?.OtherDetail1,
      otherDetail2: item?.otherDetail2 ?? item?.OtherDetail2,
      otherDetail3: item?.otherDetail3 ?? item?.OtherDetail3,
      otherDetail4: item?.otherDetail4 ?? item?.OtherDetail4,
      otherDetail5: item?.otherDetail5 ?? item?.OtherDetail5,
      otherDetail6: item?.otherDetail6 ?? item?.OtherDetail6,

      /*
       * Extra properties are intentionally preserved on the runtime object.
       * The component uses hospitalName/hospitalAddress from the same response,
       * while the existing RevenueReportRow UI fields remain unchanged.
       */
      hospitalName: String(
        item?.hospitalName ?? item?.HospitalName ?? "",
      ).trim(),

      hospitalAddress: String(
        item?.hospitalAddress ?? item?.HospitalAddress ?? "",
      ).trim(),

      hospitalCode: item?.hospitalCode ?? item?.HospitalCode,
      hospitalCity: item?.hospitalCity ?? item?.HospitalCity,
      hospitalState: item?.hospitalState ?? item?.HospitalState,
      hospitalCountry: item?.hospitalCountry ?? item?.HospitalCountry,

      totalReceipts: Number(
        item?.totalReceipts ?? item?.TotalReceipts ?? 0,
      ),
      totalRevenue: Number(item?.totalRevenue ?? item?.TotalRevenue ?? 0),
      opdRevenue: Number(item?.opdRevenue ?? item?.OPDRevenue ?? 0),
      ipdRevenue: Number(item?.ipdRevenue ?? item?.IPDRevenue ?? 0),
      opdPatients: Number(item?.opdPatients ?? item?.OPDPatients ?? 0),
      ipdPatients: Number(item?.ipdPatients ?? item?.IPDPatients ?? 0),
      doctorCount: Number(item?.doctorCount ?? item?.DoctorCount ?? 0),
    };

    return mapped as RevenueReportRow;
  }

  // ============================================================
  // MAP REVENUE ROW
  // ============================================================

  private mapRevenueRow(
    listInvoice: any,

    fullInvoice: any,

    details: any[],

    opdVisit: any,

    treatmentPlans: any[],

    currentHospitalId: number,
  ): RevenueReportRow {
    /*
     * Combine paginated and ReadById InvoiceMaster.
     *
     * Full invoice gets priority.
     */
    const invoice = {
      ...(listInvoice || {}),

      ...(fullInvoice || {}),
    };

    const status = String(invoice?.status ?? invoice?.Status ?? "").trim();

    const normalizedStatus = status.toLowerCase();

    const patientType = String(
      invoice?.patientType ?? invoice?.PatientType ?? "",
    )
      .trim()
      .toUpperCase();

    // ==========================================================
    // DOCTOR
    // ==========================================================

    let doctorId = Number(
      invoice?.doctorId ??
        invoice?.DoctorId ??
        invoice?.referredDoctorId ??
        invoice?.ReferredDoctorId ??
        invoice?.consultingDoctorId ??
        invoice?.ConsultingDoctorId ??
        0,
    );

    let doctorName = String(
      invoice?.doctorName ??
        invoice?.DoctorName ??
        invoice?.referredDoctorName ??
        invoice?.ReferredDoctorName ??
        invoice?.consultingDoctorName ??
        invoice?.ConsultingDoctorName ??
        "",
    ).trim();

    /*
     * PRIORITY 1:
     *
     * Doctor selected against an invoice service.
     *
     * Your existing billing code stores:
     *
     * staffId: Number(formData.doctorId || 0)
     */
    const invoiceDoctorDetail = (details || []).find(
      (detail: any) =>
        Number(
          detail?.staffId ??
            detail?.StaffId ??
            detail?.doctorId ??
            detail?.DoctorId ??
            0,
        ) > 0,
    );

    if (invoiceDoctorDetail) {
      const detailDoctorId = Number(
        invoiceDoctorDetail?.staffId ??
          invoiceDoctorDetail?.StaffId ??
          invoiceDoctorDetail?.doctorId ??
          invoiceDoctorDetail?.DoctorId ??
          0,
      );

      if (detailDoctorId > 0) {
        doctorId = detailDoctorId;
      }

      const detailDoctorName = String(
        invoiceDoctorDetail?.staffName ??
          invoiceDoctorDetail?.StaffName ??
          invoiceDoctorDetail?.doctorName ??
          invoiceDoctorDetail?.DoctorName ??
          "",
      ).trim();

      if (detailDoctorName) {
        doctorName = detailDoctorName;
      }
    }

    /*
     * PRIORITY 2:
     *
     * OPD doctor.
     *
     * If service doesn't have StaffId, use DoctorId stored
     * in PatientVisit.
     */
    if (doctorId <= 0 && patientType === "OPD" && opdVisit) {
      const opdDoctorId = Number(
        opdVisit?.doctorId ??
          opdVisit?.DoctorId ??
          opdVisit?.doctorID ??
          opdVisit?.DoctorID ??
          opdVisit?.consultingDoctorId ??
          opdVisit?.ConsultingDoctorId ??
          0,
      );

      if (opdDoctorId > 0) {
        doctorId = opdDoctorId;
      }

      if (!doctorName) {
        doctorName = String(
          opdVisit?.doctorName ??
            opdVisit?.DoctorName ??
            opdVisit?.consultingDoctorName ??
            opdVisit?.ConsultingDoctorName ??
            "",
        ).trim();
      }
    }

    /*
     * PRIORITY 3:
     *
     * IPD doctor.
     *
     * IPD admission stores DoctorId in TreatmentPlanMaster.
     */
    if (
      doctorId <= 0 &&
      patientType === "IPD" &&
      Array.isArray(treatmentPlans)
    ) {
      /*
       * Prefer active/latest treatment having a doctor.
       */
      const treatment = treatmentPlans
        .filter(
          (plan: any) => Number(plan?.doctorId ?? plan?.DoctorId ?? 0) > 0,
        )
        .sort(
          (a: any, b: any) =>
            Number(b?.planId ?? b?.PlanId ?? 0) -
            Number(a?.planId ?? a?.PlanId ?? 0),
        )[0];

      if (treatment) {
        doctorId = Number(treatment?.doctorId ?? treatment?.DoctorId ?? 0);

        if (!doctorName) {
          doctorName = String(
            treatment?.doctorName ??
              treatment?.DoctorName ??
              treatment?.staffName ??
              treatment?.StaffName ??
              "",
          ).trim();
        }
      }
    }

    /*
     * Last name-only fallback from invoice details.
     */
    if (!doctorName) {
      const detailWithName = (details || []).find(
        (detail: any) =>
          !!String(
            detail?.staffName ??
              detail?.StaffName ??
              detail?.doctorName ??
              detail?.DoctorName ??
              "",
          ).trim(),
      );

      if (detailWithName) {
        doctorName = String(
          detailWithName?.staffName ??
            detailWithName?.StaffName ??
            detailWithName?.doctorName ??
            detailWithName?.DoctorName ??
            "",
        ).trim();

        if (doctorId <= 0) {
          doctorId = Number(
            detailWithName?.staffId ??
              detailWithName?.StaffId ??
              detailWithName?.doctorId ??
              detailWithName?.DoctorId ??
              0,
          );
        }
      }
    }

    // ==========================================================
    // PAYMENT MODE
    // ==========================================================

    /*
     * PRIORITY 1:
     *
     * Exact PaymentMode returned by full InvoiceMaster ReadById.
     */
    let paymentMode = String(
      invoice?.paymentMode ??
        invoice?.PaymentMode ??
        invoice?.pmtMode ??
        invoice?.PmtMode ??
        invoice?.paymentType ??
        invoice?.PaymentType ??
        "",
    ).trim();

    /*
     * PRIORITY 2:
     *
     * Your payment code stores:
     *
     * OtherDetail6 = "Collected by Cash"
     * OtherDetail6 = "Collected by QR"
     * OtherDetail6 = "Collected by Payment Link"
     *
     * Extract actual method from that value.
     */
    if (!paymentMode) {
      const otherDetailValues = [
        invoice?.otherDetail6,
        invoice?.OtherDetail6,

        invoice?.otherDetail5,
        invoice?.OtherDetail5,

        invoice?.otherDetail4,
        invoice?.OtherDetail4,

        invoice?.otherDetail3,
        invoice?.OtherDetail3,

        invoice?.otherDetail2,
        invoice?.OtherDetail2,

        invoice?.otherDetail1,
        invoice?.OtherDetail1,
      ];

      const collectionDetail = otherDetailValues.find((value: any) => {
        const text = String(value || "")
          .trim()
          .toLowerCase();

        return (
          text.includes("collected by") ||
          text === "cash" ||
          text === "qr" ||
          text === "upi" ||
          text === "card" ||
          text.includes("payment link") ||
          text.includes("cheque") ||
          text.includes("credit") ||
          text.includes("debit")
        );
      });

      if (collectionDetail) {
        paymentMode = String(collectionDetail)
          .replace(/^Collected by\s*/i, "")
          .trim();
      }
    }

    /*
     * Normalize UI labels.
     *
     * This does NOT create static payment data.
     * It only normalizes values already stored in DB/API.
     */
    paymentMode = this.normalizePaymentMode(paymentMode);

    // ==========================================================
    // PATIENT
    // ==========================================================

    const patientName = String(
      invoice?.patientName ??
        invoice?.PatientName ??
        invoice?.userName ??
        invoice?.UserName ??
        invoice?.billedToName ??
        invoice?.BilledToName ??
        "",
    ).trim();

    // ==========================================================
    // DATE
    // ==========================================================

    const receivedDate =
      invoice?.paymentDate ??
      invoice?.PaymentDate ??
      invoice?.paidOn ??
      invoice?.PaidOn ??
      invoice?.receivedDate ??
      invoice?.ReceivedDate ??
      invoice?.modifiedOn ??
      invoice?.ModifiedOn ??
      invoice?.invoiceDate ??
      invoice?.InvoiceDate ??
      new Date();

    // ==========================================================
    // TOTAL RECEIVED
    // ==========================================================

    const totalReceived =
      normalizedStatus === "paid"
        ? Number(
            invoice?.invoiceAmount ??
              invoice?.InvoiceAmount ??
              invoice?.totalAmount ??
              invoice?.TotalAmount ??
              0,
          )
        : 0;

    // ==========================================================
    // FINAL ROW
    // ==========================================================

    return {
      invoiceId: Number(invoice?.invoiceId ?? invoice?.InvoiceId ?? 0),

      invoiceNumber: String(
        invoice?.invoiceNumber ?? invoice?.InvoiceNumber ?? "-",
      ),

      /*
       * Owner's HospitalId.
       */
      hospitalId: currentHospitalId,

      patientId: Number(
        invoice?.billedTo ??
          invoice?.BilledTo ??
          invoice?.patientId ??
          invoice?.PatientId ??
          0,
      ),

      patientName: patientName || "No Patient Name",

      patientType: patientType || "-",

      invoiceDate: invoice?.invoiceDate ?? invoice?.InvoiceDate ?? receivedDate,

      receivedDate,

      /*
       * Component already maps this DoctorId against
       * UserMaster_ReadByHospitalId("Doctor").
       */
      doctorId,

      doctorName: doctorName || "-",

      paymentMode: paymentMode || "-",

      status,

      totalReceived,

      prrn: invoice?.prrn ?? invoice?.PRRN,

      otherDetail1: invoice?.otherDetail1 ?? invoice?.OtherDetail1,

      otherDetail2: invoice?.otherDetail2 ?? invoice?.OtherDetail2,

      otherDetail3: invoice?.otherDetail3 ?? invoice?.OtherDetail3,

      otherDetail4: invoice?.otherDetail4 ?? invoice?.OtherDetail4,

      otherDetail5: invoice?.otherDetail5 ?? invoice?.OtherDetail5,

      otherDetail6: invoice?.otherDetail6 ?? invoice?.OtherDetail6,
    };
  }

  // ============================================================
  // PAYMENT MODE NORMALIZER
  // ============================================================

  private normalizePaymentMode(value: any): string {
    const mode = String(value ?? "").trim();

    if (!mode) {
      return "";
    }

    const normalized = mode.toLowerCase();

    if (normalized === "cash" || normalized.includes("cash")) {
      return "Cash";
    }

    if (normalized === "qr" || normalized.includes("qr")) {
      return "QR";
    }

    if (normalized === "upi" || normalized.includes("upi")) {
      return "UPI";
    }

    if (normalized.includes("payment link") || normalized === "link") {
      return "Payment Link";
    }

    if (normalized.includes("credit card")) {
      return "Credit Card";
    }

    if (normalized.includes("debit card")) {
      return "Debit Card";
    }

    if (normalized.includes("card")) {
      return "Card";
    }

    if (normalized.includes("cheque") || normalized.includes("check")) {
      return "Cheque";
    }

    if (normalized.includes("online")) {
      return "Online";
    }

    return mode;
  }

  // ============================================================
  // DATE RANGE
  // ============================================================

  private isInvoiceInsideSelectedDate(
    invoice: any,

    fromDate: string,

    toDate: string,
  ): boolean {
    const sourceDate =
      invoice?.invoiceDate ??
      invoice?.InvoiceDate ??
      invoice?.paymentDate ??
      invoice?.PaymentDate ??
      invoice?.paidOn ??
      invoice?.PaidOn ??
      invoice?.receivedDate ??
      invoice?.ReceivedDate ??
      invoice?.modifiedOn ??
      invoice?.ModifiedOn;

    if (!sourceDate) {
      return false;
    }

    const current = this.toDateOnly(sourceDate);

    const from = this.toDateOnly(fromDate);

    const to = this.toDateOnly(toDate);

    if (!current || !from || !to) {
      return false;
    }

    return (
      current.getTime() >= from.getTime() && current.getTime() <= to.getTime()
    );
  }

  // ============================================================
  // DATE HELPER
  // ============================================================

  private toDateOnly(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    /*
     * Avoid timezone conversion problem for YYYY-MM-DD.
     */
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);

      return new Date(year, month - 1, day);
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return null;
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  // ============================================================
  // SORT HELPER
  // ============================================================

  private getTime(value: string | Date): number {
    const date = new Date(value);

    return isNaN(date.getTime()) ? 0 : date.getTime();
  }
}
